import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import { PermIdentityOutlined } from '@material-ui/icons';
import {
  Bankruptcy,
  BankruptcyDate,
  Foreclosure,
  ForeclosureDate,
} from '@roc/client-portal-shared/components';
import { useStore } from '@roc/client-portal-shared/hooks';
import { citizenshipStatusOptionsRental } from '@roc/feature-loans';
import { createNumberFormatFieldNoDecimalsAndWithLimit } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import {
  createCheckboxField,
  createSelectField,
  createTextField,
} from '../../components/temp/componentBuiderTemp';
import { BorrowerFormStore } from '../../stores/common/borrowerFormStore';
import { FORM_MAX_WIDTH, LoanSubType } from '../../utils/constants';
import { FirstNameAutocompleteField } from './borrowerInformationFields';
import { RoundedBackgroundIcon } from '../common/roundedBackgroundIcon';
import { PercentagePicker } from './leverageComponents';
import { EditControls } from '../common/editControls';
import { useQuoteTypeStore } from '../../hooks/useQuoteTypeStore';
import { getExperienceOptions, isBridgeLoan } from '../../utils/utils';

const useStyles = makeStyles(theme => ({
  infoMessage: {
    fontStyle: 'italic',
    marginTop: '0rem',
    marginLeft: '1rem',
    color: 'gray',
    fontSize: '12px',
  },
}));

interface BorrowerInformationFormProps {
  store: BorrowerFormStore;
  onDeleteClick: (borrowerKey: string) => void;
  showActions?: boolean;
  displayPGField: boolean;
  showTitle?: boolean;
}

export const BorrowerInformationForm = observer(
  ({
    store,
    onDeleteClick,
    showActions = true,
    displayPGField,
    showTitle = true,
  }: BorrowerInformationFormProps) => {
    const classes = useStyles();
    const { quoteTypeStore } = useQuoteTypeStore();
    const { entityStore } = quoteTypeStore;
    const { showOwnershipPercentage, isBorrowerPercentageOwnershipEditable } = entityStore;
    const isTerm = !isBridgeLoan(quoteTypeStore.loanSubtype);
    const existingBorrower = store.existingBorrowerData;
    const experienceDisabled = isTerm
      ? Boolean(existingBorrower?.rentalExperience)
      : Boolean(existingBorrower?.experience);
    const removeButtonDisabled =
      quoteTypeStore.borrowersStore.borrowerFormStores.length <= 1;
    function saveHandler() {
      const isValid = store.runFormValidationWithMessage(
        'Please complete all the required fields'
      );
      store.setShowErrors(true);
      if (isValid) {
        store.setEditMode(false);
      }
    }

    return (
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        {showTitle && (
          <Grid
            item
            container
            spacing={2}
            justifyContent="flex-start"
            alignItems="center"
            style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
          >
            <Grid item>
              <RoundedBackgroundIcon
                Icon={PermIdentityOutlined}
                iconColor="#6D6D6D"
              />
            </Grid>
            <Grid item>
              <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                Add Borrower
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid item container spacing={2} style={{ maxWidth: FORM_MAX_WIDTH }}>
          <Grid item xs={12} sm={6}>
            <FirstNameAutocompleteField
              store={store}
              optional
              loanSubtype={quoteTypeStore.loanSubtype}
              disabled={Boolean(existingBorrower?.firstName)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Last Name',
              fieldName: 'lastName',
              testId: 'lastName',
              store,
              standaloneLabel: false,
              addMarkForRequiredFields: false,
              disabled: Boolean(existingBorrower?.lastName),
            })}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.infoMessage}>
              Adding a borrower at this stage is optional, but we will ask for
              one upon submission.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              label: 'Email',
              fieldName: 'emailAddress',
              testId: 'emailAddress',
              store,
              standaloneLabel: false,
              addMarkForRequiredFields: false,
              disabled: Boolean(existingBorrower?.emailAddress),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createNumberFormatFieldNoDecimalsAndWithLimit({
              label: 'Fico Score',
              fieldName: 'ficoScore',
              testId: 'ficoScore',
              store,
              standaloneLabel: false,
              showErrors: store.form.meta.showErrors || false,
              isRequired: true,
              disabled: Boolean(existingBorrower?.ficoScore || existingBorrower?.medianScore || existingBorrower?.ficoProvidedAtOrigination),
            })}
          </Grid>
          {isTerm && (
            <Grid item xs={12} sm={6}>
              {createSelectField({
                store,
                testId: 'citizenshipStatus',
                fieldName: 'citizenshipStatus',
                label: 'Citizenship Status',
                options: citizenshipStatusOptionsRental,
                standaloneLabel: false,
                addMarkForRequiredFields: false,
                isRequired: true,
              })}
            </Grid>
          )}
          <Grid item xs={12} sm={isTerm ? 12 : 6}>
            {createSelectField({
              store,
              testId: 'experience',
              fieldName: 'experience',
              label: 'Number of Verified Investment Properties',
              options: getExperienceOptions(quoteTypeStore.loanSubtype),
              standaloneLabel: false,
              addMarkForRequiredFields: false,
              isRequired: true,
              disabled: experienceDisabled,
            })}
          </Grid>
          {isTerm && (
            <>
              <Grid
                item
                xs={store.form.fields.bankruptcy.value === 'Y' ? 6 : 12}
              >
                <Bankruptcy store={store} />
              </Grid>
              {store.form.fields.bankruptcy.value === 'Y' && (
                <Grid item xs={6}>
                  <BankruptcyDate store={store} />
                </Grid>
              )}
              <Grid
                item
                xs={store.form.fields.foreclosure.value === 'Y' ? 6 : 12}
              >
                <Foreclosure store={store} />
              </Grid>
              {store.form.fields.foreclosure.value === 'Y' && (
                <Grid item xs={6}>
                  <ForeclosureDate store={store} />
                </Grid>
              )}
            </>
          )}
          {displayPGField && (
            <Grid item xs={showOwnershipPercentage || isTerm ? 8 : 12}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item>
                  {createCheckboxField({
                    store,
                    fieldName: 'personalGuarantor',
                    label:
                      'This borrower is a personal guarantor for the loan.',
                    color: 'primary',
                  })}
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    className={classes.infoMessage}
                    style={{ textAlign: 'center' }}
                  >
                    If yes, the borrower's credit and background reports are
                    needed. if no, only a background report is needed.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          {(showOwnershipPercentage || isTerm) && (
            <Grid item xs={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                pb={1}
              >
                <PercentagePicker
                  label="Ownership Percentage"
                  fieldName="percentageOfOwnership"
                  value={store.form.fields.percentageOfOwnership.value}
                  onChange={val => {
                    store.onFieldChange('percentageOfOwnership', val);
                    if (val > 51) {
                      store.onFieldChange('personalGuarantor', true);
                    }
                  }}
                  disabled={!isBorrowerPercentageOwnershipEditable}
                />
              </Box>
              <Typography
                variant="body2"
                className={classes.infoMessage}
                style={{ textAlign: 'center' }}
              >
                *anyone 20% ownership or higher must personally gaurantee
              </Typography>
            </Grid>
          )}
          {showActions && (
            <Grid
              item
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              style={{ padding: '1rem' }}
            >
              <EditControls
                onDeleteClick={() => onDeleteClick(store.key)}
                onSaveClick={saveHandler}
                removeButtonDisabled={removeButtonDisabled}
                onResetClick={() => store.reset()}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }
);
